/**
 * TODO: Once briefings V1 is removed, we can rename this file to `useAudio` so that it is more feature-agnostic.
 *
 * The reason we are not renaming it now is to reduce friction with the existing Briefings V1 feature
 * and thus just adding the activeFeature state and actions here.
 *
 * Once Briefings V1 is removed, we can update the name of this hook and also add a discriminated union
 * for all audio types and export feature specific hooks that converge onto the new `useAudio` hook.
 *
 * Part of reason we are not building on the new hook now is because some of the existing subcomponents
 * such as ToneSelector are used by both Briefings V1 and V2, so introduceing a new hook would mean that
 * these subcomponents would need to be either duplicated or call two state hooks.
 */

import { createStore, createHook, createContainer, createActionsHook } from 'react-sweet-state';
import type { Action } from 'react-sweet-state';

import type { Ari } from '@atlassian/ari';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys as localStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';

import { getBriefing, getNarration } from '../api/speechAPI';
import type { ActiveAudioContent } from '../types/activeAudioContent';
import type { CustomizationType } from '../types/customizationTypes';
import type { AdditionalResource } from '../types/additionalResource';
import { CustomTone, CustomLength } from '../types/customizationTypes';
import { getConfluenceContentAri } from '../utils/getConfluenceContentAri';
import type { AudioBriefingErrorType } from '../types/audioBriefingErrorType';
import type { AudioFeatureType } from '../types/audioFeatureType';
import { AudioFeature } from '../types/audioFeatureType';
import { AudioBriefingError } from '../types/audioBriefingErrorType';
import { determineErrorType } from '../utils/determineErrorType';

export type LastGeneratedBriefingConfig = {
	additionalResources: AdditionalResource[];
	customization: CustomizationType;
};
export type AudioBriefingState = {
	activeContent?: ActiveAudioContent;
	activeFeature?: AudioFeatureType;
	cloudId: string;
	activationId: string;
	locale: string;
	isLoading: boolean;
	isPlaying: boolean;
	isClosed: boolean;
	isEnded: boolean;
	hasEndBeenReached: boolean;
	hasFetched: boolean;
	playbackSpeed: number;
	error?: Error;
	errorType?: AudioBriefingErrorType;
	stream?: ReadableStream<Uint8Array>;
	streamEnded: boolean;
	requestKey: number;

	// Briefing-specific state
	customization: CustomizationType;
	additionalResources: AdditionalResource[];
	lastGeneratedBriefingConfig?: LastGeneratedBriefingConfig;
};

export type FetchAudioOptions = {
	feature: AudioFeatureType;
};

export type AudioBriefingInitialStateInput = Required<
	Pick<AudioBriefingState, 'activeContent' | 'cloudId' | 'activationId' | 'locale'>
>;

const DEFAULT_CUSTOMIZATION: CustomizationType = {
	tone: CustomTone.PROFESSIONAL,
	length: CustomLength.SHORT,
};

const DEFAULT_PLAYBACK_SPEED = 1;

const safeParseJson = (json: string) => {
	try {
		return JSON.parse(json);
	} catch (e) {
		return DEFAULT_CUSTOMIZATION;
	}
};

type AudioApiRequest = {
	activeContentAri: Ari;
	additionalResources: AdditionalResource[];
	activationId: string;
	locale: string;
	customization: CustomizationType;
};

type FeatureFetchResult = {
	stream: ReadableStream<Uint8Array>;
	patch: Omit<Partial<AudioBriefingState>, 'stream'>;
};

const _performFeatureFetch = async (
	feature: AudioFeatureType,
	req: AudioApiRequest,
): Promise<FeatureFetchResult> => {
	switch (feature) {
		case AudioFeature.BRIEFING: {
			const stream = await getBriefing(req);
			return {
				stream,
				patch: {
					lastGeneratedBriefingConfig: {
						additionalResources: req.additionalResources,
						customization: req.customization,
					},
				},
			};
		}
		case AudioFeature.NARRATION: {
			const stream = await getNarration(req);
			return { stream, patch: {} };
		}
		default:
			throw new Error('Unsupported audio feature');
	}
};

const initialAudioBriefingState: AudioBriefingState = {
	activeContent: undefined,
	activeFeature: undefined,
	customization: DEFAULT_CUSTOMIZATION,
	additionalResources: [],
	cloudId: '',
	activationId: '',
	locale: 'en-US',
	isLoading: false,
	isPlaying: false,
	isClosed: false,
	isEnded: false,
	hasEndBeenReached: false,
	hasFetched: false,
	lastGeneratedBriefingConfig: undefined,
	playbackSpeed: DEFAULT_PLAYBACK_SPEED,
	stream: undefined,
	error: undefined,
	errorType: undefined,
	streamEnded: false,
	requestKey: 0,
};

type AudioBriefingContainerProps = {
	initialAudioBriefingState: AudioBriefingInitialStateInput;
};

type AudioBriefingActions = {
	initialize: (
		initialState: AudioBriefingInitialStateInput,
	) => Action<AudioBriefingState, void, void>;
	addAdditionalResource: (
		newAdditionalResource: AdditionalResource,
	) => Action<AudioBriefingState, void, void>;
	removeAdditionalResource: (resourceToRemove: string) => Action<AudioBriefingState, void, void>;
	reset: () => Action<AudioBriefingState, void, void>;
	fetchAudio: (options: FetchAudioOptions) => Action<AudioBriefingState, void, void>;
	fetchNarration: () => Action<AudioBriefingState, void, void>;
	// Convenience wrapper for the legacy briefing feature. Delegates to `fetchAudio` with the
	// `BRIEFING` feature pre-selected so existing V1 call-sites don’t have to pass a feature param.
	// TODO: Remove this once we've migrated all call-sites to use the new `fetchAudio` API.
	fetchBriefing: () => Action<AudioBriefingState, void, void>;
	onLoadStart: (feature: AudioFeatureType) => Action<AudioBriefingState, void, void>;
	onLoadEnd: () => Action<AudioBriefingState, void, void>;
	pauseContent: () => Action<AudioBriefingState, void, void>;
	resumeContent: () => Action<AudioBriefingState, void, void>;
	setContentPlaybackSpeed: (speed: number) => Action<AudioBriefingState, void, void>;
	stopContent: () => Action<AudioBriefingState, void, void>;
	endContent: () => Action<AudioBriefingState, void, void>;
	setContentError: (error: Error) => Action<AudioBriefingState, void, void>;
	setStreamEnded: (streamEnded: boolean) => Action<AudioBriefingState, void, void>;
	setCustomization: (
		customization: Partial<CustomizationType>,
	) => Action<AudioBriefingState, void, void>;

	// Private: bumps requestKey so older in-flight requests become stale
	_invalidateInFlightRequests: () => Action<AudioBriefingState, void, void>;
};

export const audioBriefingActions: AudioBriefingActions = {
	initialize:
		(initialState: AudioBriefingInitialStateInput) =>
		async ({ setState }) => {
			const storedCustomization = localStorage.getItem(
				localStorageKeys.AI_AUDIO_BRIEFING_CUSTOMIZATION,
			);
			const parsedCustomization = storedCustomization
				? safeParseJson(storedCustomization)
				: DEFAULT_CUSTOMIZATION;

			const emptyAudioBriefingState: AudioBriefingState = {
				stream: undefined,
				streamEnded: false,
				activeContent: undefined,
				activeFeature: undefined,
				customization: parsedCustomization,
				additionalResources: [],
				locale: 'en-US',
				cloudId: '',
				activationId: '',
				isLoading: false,
				isPlaying: false,
				isClosed: false,
				isEnded: false,
				hasEndBeenReached: false,
				hasFetched: false,
				error: undefined,
				errorType: undefined,
				playbackSpeed:
					Number(localStorage.getItem(localStorageKeys.AI_AUDIO_PLAYBACK_SPEED)) ||
					DEFAULT_PLAYBACK_SPEED,
				requestKey: 0,
			};
			setState(Object.assign(emptyAudioBriefingState, initialState));
		},
	reset:
		() =>
		({ setState }) => {
			setState(initialAudioBriefingState);
		},
	_invalidateInFlightRequests:
		() =>
		({ getState, setState }) => {
			const { requestKey } = getState();
			setState({ requestKey: (requestKey ?? 0) + 1 });
		},
	addAdditionalResource:
		(newAdditionalResource: AdditionalResource) =>
		({ getState, setState }) => {
			const { additionalResources } = getState();
			setState({
				additionalResources: [...additionalResources, newAdditionalResource],
			});
		},
	removeAdditionalResource:
		(ariToRemove: string) =>
		({ getState, setState }) => {
			const { additionalResources } = getState();
			const newResourcesList = additionalResources.filter(
				(resource) => resource.ari !== ariToRemove,
			);
			setState({
				additionalResources: newResourcesList,
			});
		},
	fetchBriefing:
		() =>
		async ({ dispatch }) => {
			dispatch(audioBriefingActions.fetchAudio({ feature: AudioFeature.BRIEFING }));
		},
	fetchNarration:
		() =>
		async ({ dispatch }) => {
			dispatch(audioBriefingActions.fetchAudio({ feature: AudioFeature.NARRATION }));
		},
	fetchAudio:
		({ feature }: FetchAudioOptions) =>
		async ({ getState, setState, dispatch }) => {
			dispatch(audioBriefingActions.onLoadStart(feature));

			const {
				requestKey: capturedRequestKey,
				activeContent,
				additionalResources,
				cloudId,
				activationId,
				locale,
				customization,
			} = getState();

			if (!activeContent) {
				dispatch(
					audioBriefingActions.setContentError(
						new Error('Briefing container needs to be initialized'),
					),
				);
				return;
			}

			const activeContentAri = getConfluenceContentAri(
				cloudId,
				activeContent.id,
				activeContent.contentType,
			);

			if (!activeContentAri) {
				dispatch(audioBriefingActions.setContentError(new Error('Content ARI failed to generate')));
				return;
			}

			const isStale = () => getState().requestKey !== capturedRequestKey;

			try {
				const apiRequest = {
					activeContentAri,
					additionalResources,
					activationId,
					locale,
					customization,
				};
				const { stream, patch } = await _performFeatureFetch(feature, apiRequest);

				if (isStale() || getState().isClosed) {
					return;
				}

				setState({
					isPlaying: false,
					isEnded: false,
					hasEndBeenReached: false,
					stream,
					...patch,
				});
			} catch (e) {
				if (isStale()) {
					return;
				}
				const errorType = await determineErrorType(e);
				setState({
					error: e,
					errorType,
					stream: undefined,
				});
			} finally {
				if (!isStale()) {
					dispatch(audioBriefingActions.onLoadEnd());
				}
			}
		},
	onLoadStart:
		(feature: AudioFeatureType) =>
		({ setState, dispatch }) => {
			dispatch(audioBriefingActions._invalidateInFlightRequests());
			setState({
				activeFeature: feature,
				isLoading: true,
				hasFetched: true,
				error: undefined,
				errorType: undefined,
				streamEnded: false,
			});
		},
	onLoadEnd:
		() =>
		({ setState }) => {
			setState({ isLoading: false });
		},
	pauseContent:
		() =>
		({ setState }) => {
			setState({
				isPlaying: false,
			});
		},
	resumeContent:
		() =>
		({ setState, getState }) => {
			const { stream } = getState();
			if (!stream) {
				throw new Error('Must fetch before playing');
			}
			setState({
				isPlaying: true,
				isEnded: false,
			});
		},
	setContentPlaybackSpeed:
		(speed: number) =>
		({ setState }) => {
			localStorage.setItem(localStorageKeys.AI_AUDIO_PLAYBACK_SPEED, speed);
			setState({ playbackSpeed: speed });
		},
	stopContent:
		() =>
		async ({ dispatch, setState }) => {
			dispatch(audioBriefingActions.reset());
			setState({ isClosed: true });
		},
	endContent:
		() =>
		({ setState }) => {
			setState({
				isEnded: true,
				hasEndBeenReached: true,
				isPlaying: false,
			});
		},
	setContentError:
		(error: Error) =>
		({ setState }) => {
			setState({
				error,
				errorType: AudioBriefingError.GENERAL_ERROR,
			});
		},
	setStreamEnded:
		(streamEnded: boolean) =>
		({ setState }) => {
			setState({
				streamEnded,
			});
		},
	setCustomization:
		(newCustomization: Partial<CustomizationType>) =>
		({ setState, getState }) => {
			const { customization } = getState();
			const updatedCustomization = { ...customization, ...newCustomization };

			localStorage.setItem(
				localStorageKeys.AI_AUDIO_BRIEFING_CUSTOMIZATION,
				JSON.stringify({
					tone: updatedCustomization.tone,
					length: updatedCustomization.length,
				}),
			);
			setState({ customization: updatedCustomization });
		},
};

// Exported for tests
export const Store = createStore<AudioBriefingState, AudioBriefingActions>({
	initialState: {
		activeContent: undefined,
		customization: DEFAULT_CUSTOMIZATION,
		additionalResources: [],
		cloudId: '',
		activationId: '',
		locale: 'en-US',
		isLoading: false,
		isPlaying: false,
		isClosed: false,
		isEnded: false,
		hasEndBeenReached: false,
		hasFetched: false,
		playbackSpeed: 1,
		error: undefined,
		errorType: undefined,
		streamEnded: false,
		requestKey: 0,
	},
	actions: audioBriefingActions,
	name: 'listenerState',
});

export const AudioBriefingContainer = createContainer<
	AudioBriefingState,
	AudioBriefingActions,
	AudioBriefingContainerProps
>(Store, {
	onInit:
		() =>
		async ({ dispatch }, { initialAudioBriefingState: initialAudioPlaybackState }) => {
			void dispatch(audioBriefingActions.initialize(initialAudioPlaybackState));
		},
});

export const useAudioBriefing = createHook<AudioBriefingState, AudioBriefingActions>(Store);
export const useAudioBriefingActions = createActionsHook(Store);
